import { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  InputNumber,
  Space,
  Card,
  message,
  Row,
  Col,
  Descriptions,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { supabase } from '../lib/supabase';
import { Product } from '../types';
import { formatPrice } from '../utils/format';

const { Title } = Typography;

export default function Producten() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('producten')
        .select('*')
        .order('naam');

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('Er is een fout opgetreden bij het ophalen van de producten.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredProducts = products.filter(product =>
    product.naam.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.beschrijving?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.artikelnummer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.materiaalsoort?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns: TableColumnsType<Product> = [
    {
      title: 'Artikelnummer',
      dataIndex: 'artikelnummer',
      key: 'artikelnummer',
      sorter: (a: Product, b: Product) => (a.artikelnummer || '').localeCompare(b.artikelnummer || ''),
    },
    {
      title: 'Naam',
      dataIndex: 'naam',
      key: 'naam',
      width: 400,
      sorter: (a: Product, b: Product) => a.naam.localeCompare(b.naam),
      render: (text: string) => (
        <Tooltip title={text}>
          <div style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Prijs',
      dataIndex: 'prijs',
      key: 'prijs',
      render: (prijs: number) => formatPrice(prijs),
      sorter: (a: Product, b: Product) => a.prijs - b.prijs,
      responsive: ['sm'],
    },
    {
      title: 'Materiaal',
      dataIndex: 'materiaalsoort',
      key: 'materiaalsoort',
      responsive: ['md'],
    },
    {
      title: 'Afmetingen (mm)',
      key: 'dimensions',
      responsive: ['lg'],
      render: (_, record: Product) => {
        if (!record.lengte_mm && !record.breedte_mm && !record.hoogte_mm) return '-';
        return `${record.lengte_mm || 0} × ${record.breedte_mm || 0} × ${record.hoogte_mm || 0}`;
      }
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_: any, record: Product) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => setSelectedProduct(record)}
          type="primary"
          size={isMobile ? 'small' : 'middle'}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        style={{
          borderRadius: '8px',
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '16px' : '0'
          }}>
            <Space 
              size="middle" 
              wrap={isMobile}
              style={{ 
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'flex-start' : 'flex-end'
              }}
            >
              <Input
                placeholder="Zoek op naam, artikelnummer of materiaal"
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ 
                  width: isMobile ? '100%' : 200 
                }}
              />
              {/* The "Add Product" button has been removed as users shouldn't be able to create products */}
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={filteredProducts}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: isMobile ? false : true,
              showTotal: (total, range) => `${range[0]}-${range[1]} van ${total} producten`,
            }}
            scroll={{ x: 'max-content' }}
            size={isMobile ? 'small' : 'middle'}
          />
        </Space>
      </Card>

      <Modal
        title="Product details"
        open={!!selectedProduct}
        onCancel={() => setSelectedProduct(null)}
        footer={null}
        width={900}
      >
        {selectedProduct && (
          <div className="product-details">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ width: '20%', padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Artikelnummer</td>
                  <td style={{ width: '30%', padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.artikelnummer || '-'}</td>
                  <td style={{ width: '20%', padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Naam</td>
                  <td style={{ width: '30%', padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.naam}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Beschrijving</td>
                  <td colSpan={3} style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.beschrijving || '-'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Prijs</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{formatPrice(selectedProduct.prijs)}</td>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Verkoopeenheid</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.artikel_per || '-'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Materiaal</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.materiaalsoort || '-'}</td>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Kwaliteit</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.kwaliteit || '-'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Afmetingen</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    {(selectedProduct.lengte_mm || selectedProduct.breedte_mm || selectedProduct.hoogte_mm) 
                      ? `${selectedProduct.lengte_mm || 0} × ${selectedProduct.breedte_mm || 0} × ${selectedProduct.hoogte_mm || 0} mm` 
                      : '-'}
                  </td>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Gewicht</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.gewicht_per_stuk ? `${selectedProduct.gewicht_per_stuk} kg` : '-'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Palletisering</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.palletisering || '-'}</td>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Bedrukking</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    {selectedProduct.bedrukking === 'ja' ? 
                      <Tag color="green">Ja</Tag> : 
                      <Tag color="red">Nee</Tag>
                    }
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Levertijd</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.levertijd_dagen ? `${selectedProduct.levertijd_dagen} dagen` : '-'}</td>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>MOQ</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.moq || '-'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Nieuw/Recycled</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.status_nieuw_recycled || '-'}</td>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Eenmalig/Hergebruik</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.status_eenmalig_hergebruik || '-'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>End-Of-Life</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.status_recycled_verbranding_stort || '-'}</td>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0' }}>Verpakking Type</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{selectedProduct.verpakking_type || '-'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}
